/* eslint-disable @typescript-eslint/no-explicit-any */
import 'chartjs-adapter-date-fns'
import { ChartOptions, ChartTypeRegistry, LinearScale, Tooltip, TooltipItem } from 'chart.js'
import { DeepPartial } from 'chart.js/types/utils'
import isChromatic from 'chromatic/isChromatic'
import { add, format } from 'date-fns'
import { enUS } from 'date-fns/locale'

import { formatMetric } from '@sparkai/metric-utils'
import { isAutomatedBrowser } from '@sparkai/utils'

import { BarChartDataset, LineChartDataset } from '../Charts.types'
import {
  buildMultilineYAxisLabels,
  buildXAxisHourLabels,
  buildXAxisHourNumbersLabels,
  buildXAxisLabels,
  buildXAxisWeeklyLabels,
  buildYAxisLabels,
  convertToAmPm,
  externalTooltipHandler,
  getTicksForYAxisIgnoringNulls,
} from '../utils'

const animation = isChromatic() || isAutomatedBrowser ? false : { duration: 1000 }

function getDatasetColor(datasetIndex: number) {
  if (datasetIndex === 0) return '#FCD34D'
  if (datasetIndex === 1) return '#589FFB'
  return '#6EE7B7'
}

const parseDateString = (dateString: string): Date => {
  // Replace "a.m." and "p.m." with "AM" and "PM" respectively
  const adjustedDateString = dateString.replace('a.m.', 'AM').replace('p.m.', 'PM')

  // Parse the adjusted date string
  const dateObject = new Date(adjustedDateString)

  if (isNaN(dateObject.getTime())) {
    throw new Error('Invalid date string format')
  }

  return dateObject
}

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
//@ts-ignore
Tooltip.positioners.topFixedPositionTooltip = (_, eventPosition) => ({
  y: 0,
  x: eventPosition.x,
})

export function deepMergeChartOptions<T extends keyof ChartTypeRegistry>(
  obj1: DeepPartial<ChartOptions<T>>,
  obj2?: DeepPartial<ChartOptions<T>>
): DeepPartial<ChartOptions<T>> {
  if (!obj2) return obj1

  const result: any = Array.isArray(obj1) ? [...obj1] : { ...obj1 }

  ;(Object.keys(obj2) as (keyof typeof obj2)[]).forEach((key) => {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    //@ts-ignore
    const value1 = obj1[key]
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    //@ts-ignore
    const value2 = obj2[key]

    if (value2 && typeof value2 === 'object' && !Array.isArray(value2)) {
      result[key] = deepMergeChartOptions(value1 || ({} as any), value2 as DeepPartial<ChartOptions<T>>)
    } else {
      result[key] = value2
    }
  })

  return result as DeepPartial<ChartOptions<T>>
}

const commonTooltipProps = {
  backgroundColor: 'rgba(31, 41, 55, 1)',
  bodyFont: { family: 'Inter', weight: 'bold' },
  bodySpacing: 2,
  boxWidth: 10,
  boxHeight: 10,
  boxPadding: 3,
  multiKeyBackground: 'rgba(0, 0, 0, 0)',
  caretPadding: 20, // offsets tooltip position re:mouse pointer
  caretSize: 0,
  titleAlign: 'right',
  titleColor: 'rgba(156, 163, 175, 1)',
  titleFont: {
    family: 'Inter',
  },
  titleMarginBottom: 8,
  titleSpacing: 8,
}

// for use where data is stacked into one dataset
const stackedTooltipWithColors = {
  ...commonTooltipProps,
  /* formats display methods */
  callbacks: {
    title: (context: TooltipItem<'bar'>[]) => {
      const target = context?.[0]?.label.split(',').slice(0, 2).join(', ')
      return context?.[0]?.label ? format(new Date(target), 'MM/dd/yyyy') : ''
    },
    beforeBody: (context: [TooltipItem<'bar'>, TooltipItem<'bar'>]) => {
      return `${context.reduce((curr, value) => curr + value.parsed.y, 0)} Total`
    },
    label: function (context: TooltipItem<'bar'>) {
      const category = (context?.dataset?.label || '').toString()
      const categoryCount = context?.parsed?.y ?? 0

      if (categoryCount === 0) return null

      return `${categoryCount} ${category.toUpperCase()}`
    },
  },
  yAlign: 'bottom',
}

// for use when multiple datasets are present and we need to aggregate data for tooltip
const aggregatedTooltipWithColors = {
  ...commonTooltipProps,
  /* formats display methods */
  callbacks: {
    title: (context: TooltipItem<'bar' | 'line'>[]) => {
      const target = context?.[0]?.label.split(',').slice(0, 2).join(', ')
      return context?.[0]?.label ? format(new Date(target), 'MM/dd/yyyy') : ''
    },
    label: function (context: TooltipItem<'bar' | 'line'>) {
      const categoryCount = context?.parsed?.y ?? 0

      if (categoryCount === 0) return null

      return `${categoryCount}`
    },
  },
  yAlign: 'bottom',
}

const defaultLineLayout = {
  animation: animation,
  responsive: true,
  elements: {
    line: {
      tension: 0.5,
      borderColor: 'rgba(88, 159, 251, 1)',
      backgroundColor: 'rgba(88, 159, 251, 1)',
    },
    point: {
      borderColor: '#fff',
      borderWidth: 2,
      backgroundColor: 'rgba(88, 159, 251, 1)',
      radius: 4,
    },
  },
  layout: {
    padding: {
      right: 14,
    },
  },
}

const defaultTooltipCallbacks = {
  title: (context: TooltipItem<'bar'>[]) => {
    const splitValues = context?.[0].label.split('|')
    const isPeriod = splitValues.length > 1

    if (isPeriod) {
      const first = splitValues[0].split(',').slice(0, 2).join(', ').trim()
      const second = splitValues[1].split(',').slice(0, 2).join(', ').trim()

      return `${format(new Date(first), 'MM/dd/yyyy')} - ${format(new Date(second), 'MM/dd/yyyy')}`
    }

    const target = context?.[0]?.label.split(',').slice(0, 2).join(', ')
    return context?.[0]?.label ? format(new Date(target), 'MM/dd/yyyy') : ''
  },
}

const afterBuildTicksIgnoringNulls = {
  afterBuildTicks: (axis: LinearScale) => {
    const displayLabels = getTicksForYAxisIgnoringNulls(
      axis.chart.config.data.datasets[0] as unknown as LineChartDataset
    )
    axis.ticks = displayLabels
  },
}

const defaultGridColor = {
  grid: {
    borderColor: 'white',
    color: 'rgba(243, 244, 246, 1)',
    tickColor: 'white',
  },
}

export const DEFAULT_BAR_OPTIONS = {
  animation: animation,
  barThickness: 8,
  borderRadius: 4,
  elements: {},
  interaction: {
    intersect: false,
    mode: 'index',
  },
  layout: {
    padding: {
      right: 14,
    },
  },
  plugins: {
    legend: {
      display: false,
    },
    tooltip: {
      ...commonTooltipProps,
      /* formats display methods */
      callbacks: {
        title: (context: TooltipItem<'bar'>[]) => {
          const target = context?.[0]?.label.split(',').slice(0, 2).join(', ')
          return context?.[0]?.label ? format(new Date(target), 'MM/dd/yyyy') : ''
        },
        beforeBody: (context: [TooltipItem<'bar'>, TooltipItem<'bar'>]) => {
          return `${(context?.[0]?.parsed?.y ?? 0) + (context?.[1]?.parsed?.y ?? 0)} Total`
        },
        label: function (context: TooltipItem<'bar'>) {
          const category = context?.dataset?.label || ''
          const categoryCount = context?.parsed?.y ?? 0
          const total = (context?.parsed?._stacks?.y?.[0] ?? 0) + (context?.parsed?._stacks?.y?.[1] ?? 0) || 1
          const proposed = ((categoryCount / total) * 100).toFixed(0)
          const percentage = isNaN(parseFloat(proposed)) ? 0 : proposed

          return `${categoryCount} ${category} (${percentage}%)`
        },
        footer: (context: TooltipItem<'bar'>[]) => {
          const ctx = context?.[0]

          const meta = (ctx.dataset as any).meta?.[ctx.dataIndex]
          const metaDisplay = (ctx.dataset as any)?.metaDisplayName

          if (meta) {
            return `${meta} ${metaDisplay}`
          }
        },
      },
    },
  },
  responsive: true,
  scales: {
    x: {
      afterBuildTicks: (axis: LinearScale) => {
        axis.ticks = buildXAxisLabels(axis.chart.config.data.labels as Date[])
      },
      adapters: {
        date: {
          locale: enUS,
        },
      },
      autoSkip: true,
      stacked: true,
      grid: {
        borderColor: 'white',
        color: 'rgba(255,255,255, 1)',
        tickColor: 'white',
      },
      type: 'time',
      time: {
        unit: 'day',
        displayFormats: {
          day: 'MM/dd',
        },
      },
    },
    y: {
      afterBuildTicks: (axis: LinearScale) => {
        const datasets = axis.chart.config.data.datasets as { data: number[] }[]

        if (datasets.length === 0) return

        const syntheticDatasets = {
          data: datasets[0].data.map((_, index) =>
            datasets.reduce((sum, dataset) => sum + (dataset.data[index] || 0), 0)
          ),
        }
        axis.ticks = buildYAxisLabels(syntheticDatasets as unknown as BarChartDataset)
      },
      stacked: true,
      grid: {
        borderColor: 'white',
        color: 'rgba(243, 244, 246, 1)',
        tickColor: 'white',
      },
      ticks: {
        callback(tickValue: number) {
          return tickValue.toLocaleString('en-US')
        },
      },
    },
  },
}

export const CURRENT_DAY_BAR_OPTIONS = {
  animation: animation,
  barThickness: 8,
  elements: {},
  borderRadius: (ctx: any) => {
    const dataPoint = ctx.dataIndex
    const datasetIndex = ctx.datasetIndex

    const values = ctx.chart.data.datasets.map((dataset: any) => dataset.data[dataPoint])
    const activeValues = values.filter((val: any) => val !== null && val !== undefined && val !== 0).length

    if (activeValues === 1) {
      return {
        topLeft: 10,
        topRight: 10,
        bottomLeft: 10,
        bottomRight: 10,
      }
    }

    const isFirst =
      values.findIndex((val: any) => val !== null && val !== undefined && val !== 0) === datasetIndex
    const isLast =
      values
        .slice()
        .reverse()
        .findIndex((val: any) => val !== null && val !== undefined && val !== 0) ===
      values.length - 1 - datasetIndex

    if (isFirst) return { bottomLeft: 10, bottomRight: 10 }
    if (isLast) return { topLeft: 10, topRight: 10 }

    return { topLeft: 0, topRight: 0, bottomLeft: 0, bottomRight: 0 }
  },
  interaction: {
    intersect: true,
    mode: 'index',
  },
  layout: {
    padding: {
      right: 14,
    },
  },
  plugins: {
    legend: {
      display: false,
    },
    tooltip: {
      ...commonTooltipProps,
      displayColors: false,
      /* formats display methods */
      callbacks: {
        title: (context: TooltipItem<'bar'>[]) => {
          const hour = parseInt(context?.[0]?.label)
          const lastHour = hour + 1 === 24
          const startHour = convertToAmPm(hour)
          const endHour = convertToAmPm(lastHour ? 0 : hour + 1)
          return `${startHour} - ${endHour}`
        },
        beforeBody: (context: [TooltipItem<'bar'>, TooltipItem<'bar'>]) => {
          const currentTotal = context.reduce((acc, curr) => acc + (curr?.parsed?.y ?? 0), 0)

          const previousTotal = context.reduce(
            (acc, curr) => acc + (curr?.dataset?.data[curr.dataIndex - 1] ?? 0),
            0
          )

          const isPrediction = (context[0].dataset as any).predictionIndex <= context[0].dataIndex

          const previousFinalValue = Number.isInteger(previousTotal)
            ? previousTotal
            : parseFloat(previousTotal.toFixed(2))

          const isNeutral = currentTotal === previousFinalValue
          if (isNeutral || previousFinalValue === 0) return [currentTotal, 0, 'neutral', isPrediction]

          const variation = ((currentTotal - previousFinalValue) / previousFinalValue) * 100
          const variationStatus = variation > 0 ? 'positive' : 'negative'
          const percentage = Number.isInteger(variation)
            ? Math.abs(variation)
            : Math.abs(parseFloat(variation.toFixed(2)))

          return [currentTotal, percentage, variationStatus, isPrediction]
        },
        label: function (context: TooltipItem<'bar'>) {
          if (context.datasetIndex !== 0) return null

          const currentIndex = context.dataIndex
          const allDatasets = context.chart.data.datasets

          const currentTotal = allDatasets.reduce((acc, dataset) => {
            return acc + ((dataset.data[currentIndex] as number) ?? 0)
          }, 0)

          const isPrediction = (context.dataset as any).predictionIndex <= context.dataIndex

          const hasSurgeAlert = (context.dataset as any).alerts.surgeAlertHours.includes(currentIndex)
          const hasCapacityAlert = (context.dataset as any).alerts.capacityAlertsHours.includes(currentIndex)

          const list: {
            label: string
            total: number
            color?: string
            iconName?: string
            values: any[]
            sortIndex?: number
          }[] = []

          if (hasSurgeAlert) {
            list.push({
              label: 'Surge Alert',
              total: 0,
              iconName: 'surge-alert',
              values: [],
              sortIndex: -2,
            })
          }

          if (hasCapacityAlert) {
            list.push({
              label: 'Capacity Alert',
              total: 0,
              iconName: 'capacity-alert',
              values: [],
              sortIndex: -1,
            })
          }

          if (!isPrediction) {
            const sortOrder = {
              'High Priority': 1,
              'Low Priority': 2,
              'Timed Out': 3,
              Rejected: 4,
              Recycled: 5,
            }

            const color = {
              'High Priority': '#6EE7B7',
              'Low Priority': '#5A86AE',
              Recycled: '#7FB6B1',
              Rejected: '#EDC948',
              'Timed Out': '#B6BDC6',
            }

            const datasetItems = allDatasets
              .map((dataset) => {
                const value = (dataset.data[currentIndex] as number) ?? 0
                const percentage =
                  currentTotal === 0 ? 0 : parseFloat(((value / currentTotal) * 100).toFixed(0))

                const label = dataset.label
                  ?.replace('High Priority Queue', 'High Priority')
                  .replace('Low Priority Queue', 'Low Priority')

                return {
                  label: `${label}: ${percentage}% (${value})`,
                  total: value,
                  color: color[label as keyof typeof color],
                  values: [],
                  sortIndex: label ? sortOrder[label as keyof typeof sortOrder] : 999,
                }
              })
              .filter((item) => item.total > 0)
              .sort((a, b) => a.sortIndex - b.sortIndex)

            list.push(...datasetItems)
          }

          const msOnline = (context.dataset as any).msOnline[currentIndex]
          const msStatus = !isPrediction ? 'online' : 'scheduled'

          list.push({
            label: `Mission Specialists ${msStatus}: ${msOnline}`,
            total: msOnline,
            values: [],
            sortIndex: 999,
          })

          return list
        },
      },
      enabled: false,
      external: (context: any) =>
        externalTooltipHandler(context, {
          customTotalTitle: 'Total Engagements',
          customTotalPredictionTitle: 'Predicted Engagements',
          noBodyLabelEllipsis: true,
          moveFirstBar: 20,
          reverseTrendingComparison: true,
        }),
    },
  },
  responsive: true,
  scales: {
    x: {
      autoSkip: true,
      stacked: true,
      grid: {
        borderColor: 'white',
        color: 'rgba(255,255,255, 1)',
        tickColor: 'white',
      },
      ticks: {
        padding: 32,
      },
    },
    y: {
      afterBuildTicks: (axis: LinearScale) => {
        const datasets = axis.chart.config.data.datasets as { data: number[] }[]

        if (datasets.length === 0) return

        const syntheticDatasets = {
          data: datasets[0].data.map((_, index) =>
            datasets.reduce((sum, dataset) => sum + (dataset.data[index] || 0), 0)
          ),
        }
        axis.ticks = buildYAxisLabels(syntheticDatasets as unknown as BarChartDataset)
      },
      stacked: true,
      grid: {
        borderColor: 'white',
        color: 'rgba(243, 244, 246, 1)',
        tickColor: 'white',
      },
      ticks: {
        callback(tickValue: number) {
          return tickValue.toLocaleString('en-US')
        },
      },
    },
  },
}

export const DAILY_BAR_OPTIONS = {
  ...DEFAULT_BAR_OPTIONS,
  plugins: {
    ...DEFAULT_BAR_OPTIONS.plugins,
    tooltip: {
      ...DEFAULT_BAR_OPTIONS.plugins.tooltip,
      /* formats display methods */
      callbacks: {
        ...DEFAULT_BAR_OPTIONS.plugins.tooltip.callbacks,
        title: (context: TooltipItem<'bar'>[]) => {
          const target = context?.[0]?.label
          const dateTarget = target ? parseDateString(target) : new Date()
          const startHour = format(dateTarget, 'ha')
          const endHour = format(add(dateTarget, { hours: 1 }), 'ha')
          const hourRange = `${startHour} - ${endHour}`

          return hourRange
        },
      },
    },
  },
  scales: {
    ...DEFAULT_BAR_OPTIONS.scales,
    x: {
      ...DEFAULT_BAR_OPTIONS.scales.x,
      afterBuildTicks: (axis: LinearScale) => {
        axis.ticks = buildXAxisHourLabels(axis.chart.config.data.labels as Date[], 4)
      },
      time: {
        ...DEFAULT_BAR_OPTIONS.scales.x.time,
        unit: 'hour',
        displayFormats: {
          ...DEFAULT_BAR_OPTIONS.scales.x.time.displayFormats,
          day: 'h a',
        },
      },
    },
  },
}

export const WEEKLY_BAR_OPTIONS = () => {
  let weeklyHelpLabels: string[] = []

  return {
    ...DEFAULT_BAR_OPTIONS,
    plugins: {
      ...DEFAULT_BAR_OPTIONS.plugins,
      tooltip: {
        ...DEFAULT_BAR_OPTIONS.plugins.tooltip,
        /* formats display methods */
        callbacks: {
          beforeBody: (context: [TooltipItem<'bar'>, TooltipItem<'bar'>]) => {
            return `${(context?.[0]?.parsed?.y ?? 0) + (context?.[1]?.parsed?.y ?? 0)} Total`
          },
          label: function (context: TooltipItem<'bar'>) {
            const category = context?.dataset?.label || ''
            const categoryCount = context?.parsed?.y ?? 0
            const total =
              (context?.parsed?._stacks?.y?.[0] ?? 0) + (context?.parsed?._stacks?.y?.[1] ?? 0) || 1
            const proposed = ((categoryCount / total) * 100).toFixed(0)
            const percentage = isNaN(parseFloat(proposed)) ? 0 : proposed

            return `${categoryCount} ${category} (${percentage}%)`
          },
          footer: (context: TooltipItem<'bar'>[]) => {
            const ctx = context?.[0]

            const meta = (ctx.dataset as any).meta?.[ctx.dataIndex]
            const metaDisplay = (ctx.dataset as any)?.metaDisplayName

            if (meta) {
              return `${meta} ${metaDisplay}`
            }
          },
          title: (context: TooltipItem<'bar'>[]) => {
            const target = context?.[0]?.label
            const split = target.split('|')
            const startDate = new Date(split[0].trim())
            const endDate = new Date(split[1].trim())
            const start = format(startDate, 'MM/dd/yyyy')
            const end = format(endDate, 'MM/dd/yyyy')

            return `${start} - ${end}`
          },
        },
      },
    },
    scales: {
      ...DEFAULT_BAR_OPTIONS.scales,
      x: {
        ...DEFAULT_BAR_OPTIONS.scales.x,
        afterBuildTicks: (axis: LinearScale) => {
          weeklyHelpLabels = buildXAxisWeeklyLabels(axis.chart.config.data.labels as string[])
        },
        ticks: {
          callback(tickValue: number) {
            return weeklyHelpLabels[tickValue]
          },
        },
        type: 'category',
        time: null,
        adapters: null,
      },
    },
  }
}

const formatPercentage = (value: number, maxDecimalLength = 2) => {
  if (isNaN(value)) return '0%'

  return new Intl.NumberFormat('en-US', {
    style: 'percent',
    minimumFractionDigits: 0,
    maximumFractionDigits: maxDecimalLength,
  }).format(value)
}

function formatBarAndLineTooltipLabel(context: TooltipItem<'bar'>) {
  let result = ''

  const getCorrectedUncorrectedFromContext = (context: TooltipItem<'bar'>) => {
    const corrected = (context?.chart?.config?.data?.datasets?.[1]?.data?.[context.dataIndex] ?? 0) as number
    const uncorrected = (context?.chart?.config?.data?.datasets?.[2]?.data?.[context.dataIndex] ??
      0) as number
    const total = corrected + uncorrected
    return [corrected, uncorrected, total]
  }

  // the datasets must be loaded in the orider [ line, corrected, uncorrected ] to display on canvas properly
  // the tooltip design doesn't match the above, so we need to handle the discrepancy here
  //    - design order is title > corrected count > uncorrected count > percentage change
  // each dataset gets it's own label, but we can re-arrange as needed since all chart context is on each label
  switch (context.datasetIndex) {
    // corrected label
    case 0: {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const [corrected, , total] = getCorrectedUncorrectedFromContext(context)
      const percentage = formatPercentage(corrected / total)
      result = `${corrected} Corrected (${percentage})`
      break
    }
    // uncorrected label
    case 1: {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const [, uncorrected, total] = getCorrectedUncorrectedFromContext(context)
      const percentage = formatPercentage(uncorrected / total)
      result = `${uncorrected} Uncorrected (${percentage})`
      break
    }
    // percentage change label
    case 2: {
      const value = (context?.chart?.config?.data?.datasets?.[0]?.data?.[context.dataIndex] ?? 0) as number
      result = `${value ?? 0}% Annotations Corrected`

      break
    }
  }

  return result
}

export const DEFAULT_BAR_AND_LINE_OPTIONS = {
  animation: animation,
  barThickness: 8,
  borderRadius: 4,
  elements: {
    line: {
      tension: 0.5,
    },
    point: { borderColor: '#fff', borderWidth: 2, radius: 4 },
  },
  interaction: {
    intersect: false,
    mode: 'index',
  },
  layout: {
    padding: {
      right: 14,
    },
  },
  plugins: {
    legend: {
      display: false,
    },
    tooltip: {
      ...commonTooltipProps,
      /* formats display methods */
      callbacks: {
        title: (context: TooltipItem<'bar'>[]) => {
          const target = context?.[0]?.label.split(',').slice(0, 2).join(', ')
          return context?.[0]?.label ? format(new Date(target), 'MM/dd/yyyy') : ''
        },
        beforeBody: (context: [TooltipItem<'bar'>, TooltipItem<'bar'>]) => {
          const annotationCount = context
            .filter((c) => c.dataset.type === 'bar')
            .reduce((prev, curr) => (prev += curr.parsed.y ?? 0), 0)

          return `${annotationCount} Annotations`
        },
        label: function (context: TooltipItem<'bar'>) {
          return formatBarAndLineTooltipLabel(context)
        },
        labelColor: function (context: TooltipItem<'bar'>) {
          return {
            borderColor: 'rgb(0, 0, 0, 0)',
            backgroundColor: getDatasetColor(context.datasetIndex),
            borderRadius: context.datasetIndex === 2 ? 4 : 2,
            borderWidth: 2,
          }
        },
        footer: (context: TooltipItem<'bar'>[]) => {
          const ctx = context?.[0]

          const meta = (ctx.dataset as any).meta?.[ctx.dataIndex]
          const metaDisplay = (ctx.dataset as any)?.metaDisplayName

          if (meta) {
            return `${meta} ${metaDisplay}`
          }
        },
      },
    },
  },
  responsive: true,
  scales: {
    x: {
      afterBuildTicks: (axis: LinearScale) => {
        axis.ticks = buildXAxisLabels(axis.chart.config.data.labels as Date[])
      },
      adapters: {
        date: {
          locale: enUS,
        },
      },
      autoSkip: true,
      stacked: true,
      grid: {
        borderColor: 'white',
        color: 'rgba(255,255,255, 1)',
        tickColor: 'white',
      },
      type: 'time',
      time: {
        unit: 'day',
        displayFormats: {
          day: 'MM/dd',
        },
      },
    },
    // displays the total annotation count scale (bars)
    leftYAxis: {
      type: 'linear',
      position: 'left',
      grid: {
        drawOnChartArea: false,
        borderColor: 'white',
        color: 'rgba(243, 244, 246, 1)',
        tickColor: 'white',
      },
      afterBuildTicks(axis: LinearScale) {
        const datasets = axis.chart.config.data.datasets as { data: number[]; yAxisID?: string }[]
        const barDatasets = datasets.filter((ds) => ds.yAxisID === 'leftYAxis')

        if (barDatasets.length === 0) return

        const syntheticDatasets = {
          data: barDatasets[0].data.map((_, index) =>
            barDatasets.reduce((sum, dataset) => sum + (dataset.data[index] || 0), 0)
          ),
        }
        axis.ticks = buildYAxisLabels(syntheticDatasets as unknown as BarChartDataset)
      },
    },
    // displays the corrected percentage trendline scale
    rightYAxis: {
      type: 'linear',
      position: 'right',
      grid: {
        drawOnChartArea: false,
        borderColor: 'white',
        color: 'rgba(243, 244, 246, 1)',
        tickColor: 'white',
      },
      afterBuildTicks(axis: LinearScale) {
        const data = axis.chart.config.data.datasets[0]
        const ticks = getTicksForYAxisIgnoringNulls(data as unknown as LineChartDataset)
        axis.ticks = ticks
        axis.min = ticks[0].value
        axis.max = ticks[2].value
      },
      ticks: {
        callback(tickValue: number) {
          return `${formatPercentage(tickValue / 100, 0)}`
        },
      },
    },
  },
}

export const DAILY_BAR_AND_LINE_OPTIONS = {
  ...DEFAULT_BAR_AND_LINE_OPTIONS,
  plugins: {
    ...DEFAULT_BAR_AND_LINE_OPTIONS.plugins,
    tooltip: {
      ...DEFAULT_BAR_AND_LINE_OPTIONS.plugins.tooltip,
      /* formats display methods */
      callbacks: {
        ...DEFAULT_BAR_AND_LINE_OPTIONS.plugins.tooltip.callbacks,
        title: (context: TooltipItem<'bar'>[]) => {
          const target = context?.[0]?.label
          const dateTarget = target ? parseDateString(target) : new Date()
          const startHour = format(dateTarget, 'ha')
          const endHour = format(add(dateTarget, { hours: 1 }), 'ha')
          const hourRange = `${startHour} - ${endHour}`

          return hourRange
        },
      },
    },
  },
  scales: {
    ...DEFAULT_BAR_AND_LINE_OPTIONS.scales,
    x: {
      ...DEFAULT_BAR_AND_LINE_OPTIONS.scales.x,
      afterBuildTicks: (axis: LinearScale) => {
        axis.ticks = buildXAxisHourLabels(axis.chart.config.data.labels as Date[], 4)
      },
      time: {
        ...DEFAULT_BAR_AND_LINE_OPTIONS.scales.x.time,
        unit: 'hour',
        displayFormats: {
          ...DEFAULT_BAR_AND_LINE_OPTIONS.scales.x.time.displayFormats,
          day: 'h a',
        },
      },
    },
  },
}

export const DAILY_24H_BAR_AND_LINE_OPTIONS = {
  ...DAILY_BAR_AND_LINE_OPTIONS,
  plugins: {
    ...DAILY_BAR_AND_LINE_OPTIONS.plugins,
    tooltip: {
      ...DAILY_BAR_AND_LINE_OPTIONS.plugins.tooltip,
      callbacks: {
        ...DAILY_BAR_AND_LINE_OPTIONS.plugins.tooltip.callbacks,
        title: (context: TooltipItem<'bar'>[]) => {
          const target = context?.[0]?.label
          const dateTarget = target ? parseDateString(target) : new Date()
          const startHour = format(dateTarget, 'HH:mm')
          const endHour = format(add(dateTarget, { hours: 1 }), 'HH:mm')

          return `${startHour} - ${endHour}`
        },
      },
    },
  },
  scales: {
    ...DAILY_BAR_AND_LINE_OPTIONS.scales,
    x: {
      ...DAILY_BAR_AND_LINE_OPTIONS.scales.x,
      afterBuildTicks: (axis: LinearScale) => {
        axis.ticks = buildXAxisHourLabels(axis.chart.config.data.labels as Date[], 4, '24h')
      },
    },
  },
}

export const WEEKLY_BAR_AND_LINE_OPTIONS = () => {
  let weeklyHelpLabels: string[] = []

  return {
    ...DEFAULT_BAR_AND_LINE_OPTIONS,
    plugins: {
      ...DEFAULT_BAR_AND_LINE_OPTIONS.plugins,
      tooltip: {
        ...DEFAULT_BAR_AND_LINE_OPTIONS.plugins.tooltip,
        /* formats display methods */
        callbacks: {
          beforeBody: (context: [TooltipItem<'bar'>, TooltipItem<'bar'>]) => {
            return `${(context?.[0]?.parsed?.y ?? 0) + (context?.[1]?.parsed?.y ?? 0)} Total`
          },
          label: function (context: TooltipItem<'bar'>) {
            const category = context?.dataset?.label || ''
            const categoryCount = context?.parsed?.y ?? 0
            const total =
              (context?.parsed?._stacks?.y?.[0] ?? 0) + (context?.parsed?._stacks?.y?.[1] ?? 0) || 1
            const proposed = ((categoryCount / total) * 100).toFixed(0)
            const percentage = isNaN(parseFloat(proposed)) ? 0 : proposed

            return `${categoryCount} ${category} (${percentage}%)`
          },
          footer: (context: TooltipItem<'bar'>[]) => {
            const ctx = context?.[0]

            const meta = (ctx.dataset as any).meta?.[ctx.dataIndex]
            const metaDisplay = (ctx.dataset as any)?.metaDisplayName

            if (meta) {
              return `${meta} ${metaDisplay}`
            }
          },
          title: (context: TooltipItem<'bar'>[]) => {
            const target = context?.[0]?.label
            const split = target.split('|')
            const startDate = new Date(split[0].trim())
            const endDate = new Date(split[1].trim())
            const start = format(startDate, 'MM/dd/yyyy')
            const end = format(endDate, 'MM/dd/yyyy')

            return `${start} - ${end}`
          },
        },
      },
    },
    scales: {
      ...DEFAULT_BAR_AND_LINE_OPTIONS.scales,
      x: {
        ...DEFAULT_BAR_AND_LINE_OPTIONS.scales.x,
        afterBuildTicks: (axis: LinearScale) => {
          weeklyHelpLabels = buildXAxisWeeklyLabels(axis.chart.config.data.labels as string[])
        },
        ticks: {
          callback(tickValue: number) {
            return weeklyHelpLabels[tickValue]
          },
        },
        type: 'category',
        time: null,
        adapters: null,
      },
    },
  }
}

export const DEFAULT_LINE_OPTIONS = {
  ...defaultLineLayout,
  plugins: {
    legend: {
      display: false,
    },
    tooltip: {
      ...commonTooltipProps,
      displayColors: false,
      /* formats display methods */
      callbacks: {
        ...defaultTooltipCallbacks,
      },
    },
  },
  scales: {
    x: {
      display: false,
    },
    y: {
      afterBuildTicks: (axis: LinearScale) => {
        const displayLabels = buildYAxisLabels(
          axis.chart.config.data.datasets[0] as unknown as LineChartDataset
        )
        axis.ticks = displayLabels
      },
      ...defaultGridColor,
      min: 0,
      ticks: {
        callback: (tickValue: string | number): string => `${tickValue}s`,
      },
    },
  },
}

export const STACKED_LINE_OPTIONS_DEFAULT = {
  animation: animation,
  responsive: true,
  elements: {
    line: {
      tension: 0.5,
    },
    point: {
      radius: 0,
      hitRadius: 0,
      hoverRadius: 0,
    },
  },
  interaction: {
    mode: 'nearest',
    axis: 'x',
    intersect: false,
  },
  plugins: {
    legend: {
      display: false,
    },
    tooltip: stackedTooltipWithColors,
  },
  scales: {
    x: {
      afterBuildTicks: (axis: LinearScale) => {
        axis.ticks = buildXAxisLabels(axis.chart.config.data.labels as Date[])
      },
      type: 'time',
      autoSkip: true,
      time: {
        displayFormats: {
          day: 'MM/dd',
        },
        adapters: {
          locale: '',
        },
      },
      grid: {
        display: false,
      },
    },
    y: {
      ...defaultGridColor,
      stacked: true,
      afterBuildTicks: (axis: LinearScale) => {
        axis.ticks = [0, axis.max / 2, axis.max].map((v) => ({ value: v }))
      },
      ticks: {
        callback: (value: string) => parseInt(value).toLocaleString('en-US'),
      },
      min: 0,
    },
  },
}

export const SECONDS_UNIT_LINE_OPTIONS = {
  spanGaps: 1000 * 60 * 60 * 24,
  ...defaultLineLayout,
  plugins: {
    legend: {
      display: false,
    },
    tooltip: {
      ...commonTooltipProps,
      displayColors: false,
      /* formats display methods */
      callbacks: {
        ...defaultTooltipCallbacks,
        label: function (context: TooltipItem<'bar'>) {
          const category = context?.dataset?.label || ''
          const categoryCount = context?.parsed?.y ?? 0

          return `${categoryCount}s ${category}`
        },

        footer: (context: TooltipItem<'bar'>[]) => {
          const ctx = context?.[0]
          if (!ctx) return undefined

          const meta = (ctx?.dataset as any)?.meta?.[ctx?.dataIndex]

          if (!meta) return undefined

          const metaOptions = (ctx.dataset as any)?.metaOptions as {
            successLabel: string
            failedLabel: string
          }

          const successRatio = Math.round((meta.success / meta.total) * 100)
          const failedRatio = Math.round((meta.failed / meta.total) * 100)

          return [
            `${meta.total} Total`,
            `${meta.success} ${metaOptions.successLabel} (${successRatio || 0}%)`,
            `${meta.failed} ${metaOptions.failedLabel} (${failedRatio || 0}%)`,
          ]
        },
      },
    },
  },
  scales: {
    x: {
      display: false,
    },
    y: {
      ...afterBuildTicksIgnoringNulls,
      ...defaultGridColor,
      ticks: {
        callback: (tickValue: string | number): string => `${tickValue}s`,
      },
    },
  },
}

export const SECONDS_UNIT_DAILY_LINE_OPTIONS = {
  ...SECONDS_UNIT_LINE_OPTIONS,
  spanGaps: 1000 * 60 * 60,

  plugins: {
    ...SECONDS_UNIT_LINE_OPTIONS.plugins,
    tooltip: {
      ...SECONDS_UNIT_LINE_OPTIONS.plugins.tooltip,
      /* formats display methods */
      callbacks: {
        ...SECONDS_UNIT_LINE_OPTIONS.plugins.tooltip.callbacks,
        title: (context: TooltipItem<'bar'>[]) => {
          const target = context?.[0]?.label
          const dateTarget = target ? parseDateString(target) : new Date()
          const startHour = format(dateTarget, 'ha')
          const endHour = format(add(dateTarget, { hours: 1 }), 'ha')
          const hourRange = `${startHour} - ${endHour}`

          return hourRange
        },
      },
    },
  },
}

export const TIME_TO_COMPLETE_OPTIONS = {
  spanGaps: 1000 * 60 * 60 * 24,
  ...defaultLineLayout,
  plugins: {
    legend: {
      display: false,
    },
    tooltip: {
      ...commonTooltipProps,
      displayColors: false,
      /* formats display methods */
      callbacks: {
        ...defaultTooltipCallbacks,
        label: function (context: TooltipItem<'bar'>) {
          const category = context?.dataset?.label || ''
          const categoryCount = context?.parsed?.y ?? 0

          if (categoryCount === 0) return null

          return `${formatMetric(categoryCount, 'second')} ${category}`
        },
      },
    },
  },
  scales: {
    x: {
      afterBuildTicks: (axis: LinearScale) => {
        axis.ticks = buildXAxisLabels(axis.chart.config.data.labels as Date[])
      },
      type: 'time',
      autoSkip: true,
      time: {
        displayFormats: {
          day: 'MM/dd',
        },
        adapters: {
          locale: '',
        },
      },
      grid: {
        display: false,
      },
    },
    y: {
      ...afterBuildTicksIgnoringNulls,
      ...defaultGridColor,
      ticks: {
        callback: (tickValue: number): string => formatMetric(tickValue, 'second'),
      },
    },
  },
}

export const TIME_TO_COMPLETE_DAILY_OPTIONS = {
  ...TIME_TO_COMPLETE_OPTIONS,
  spanGaps: 1000 * 60 * 60,
  plugins: {
    ...TIME_TO_COMPLETE_OPTIONS.plugins,
    tooltip: {
      ...TIME_TO_COMPLETE_OPTIONS.plugins.tooltip,
      /* formats display methods */
      callbacks: {
        ...TIME_TO_COMPLETE_OPTIONS.plugins.tooltip.callbacks,
        title: (context: TooltipItem<'bar'>[]) => {
          const target = context?.[0]?.label
          const dateTarget = target ? parseDateString(target) : new Date()
          const startHour = format(dateTarget, 'ha')
          const endHour = format(add(dateTarget, { hours: 1 }), 'ha')
          const hourRange = `${startHour} - ${endHour}`

          return hourRange
        },
      },
    },
  },
}

export const TIME_TO_COMPLETE_WEEKLY_OPTIONS = () => {
  let weeklyHelpLabels: string[] = []

  return {
    ...TIME_TO_COMPLETE_OPTIONS,
    scales: {
      ...TIME_TO_COMPLETE_OPTIONS.scales,
      x: {
        ...TIME_TO_COMPLETE_OPTIONS.scales.x,
        afterBuildTicks: (axis: LinearScale) => {
          weeklyHelpLabels = buildXAxisWeeklyLabels(axis.chart.config.data.labels as string[])
        },
        ticks: {
          callback(tickValue: number) {
            return weeklyHelpLabels[tickValue]
          },
        },
        type: 'category',
        time: null,
        adapters: null,
      },
      y: {
        ...afterBuildTicksIgnoringNulls,
        ...defaultGridColor,
        ticks: {
          callback: (tickValue: string | number): string => {
            if (typeof tickValue === 'string') return `${parseFloat(tickValue).toFixed(2)}s`
            return `${tickValue.toFixed(2)}s`
          },
        },
      },
    },
  }
}

export const PERCENT_UNIT_LINE_OPTIONS = {
  spanGaps: 1000 * 60 * 60 * 24,
  ...defaultLineLayout,
  plugins: {
    legend: {
      display: false,
    },
    tooltip: {
      ...commonTooltipProps,
      displayColors: false,
      /* formats display methods */
      callbacks: {
        ...defaultTooltipCallbacks,
        label: function (context: TooltipItem<'bar'>) {
          const category = context?.dataset?.label || ''
          const categoryCount = context?.parsed?.y ?? 0

          if (categoryCount === 0) return null

          return `${categoryCount}% ${category}`
        },
      },
    },
  },
  scales: {
    x: {
      display: false,
    },
    y: {
      ...afterBuildTicksIgnoringNulls,
      ...defaultGridColor,
      ticks: {
        font: {},
        callback: (tickValue: string | number): string => {
          if (typeof tickValue === 'string') return `${tickValue}%`
          return new Intl.NumberFormat('en-US', {
            style: 'unit',
            unit: 'percent',
            maximumFractionDigits: 1,
          }).format(tickValue)
        },
      },
    },
  },
}
export const PERCENT_UNIT_ACCURACY_LINE_OPTIONS = Object.assign({}, PERCENT_UNIT_LINE_OPTIONS)

PERCENT_UNIT_ACCURACY_LINE_OPTIONS.scales.y.ticks.font = {
  family: 'Roboto Mono',
}

PERCENT_UNIT_ACCURACY_LINE_OPTIONS.scales.y.ticks.callback = (tickValue: string | number): string => {
  if (typeof tickValue === 'string') return `${tickValue}%`
  return new Intl.NumberFormat('en-US', {
    style: 'unit',
    unit: 'percent',
    maximumFractionDigits: 1,
  })
    .format(tickValue)
    .padStart(5, ' ')
}

export const PERCENT_UNIT_DAILY_LINE_OPTIONS = {
  spanGaps: 1000 * 60 * 60 * 24,
  ...defaultLineLayout,
  plugins: {
    legend: {
      display: false,
    },
    tooltip: {
      ...commonTooltipProps,
      displayColors: false,
      /* formats display methods */
      callbacks: {
        ...defaultTooltipCallbacks,
        title: (context: TooltipItem<'bar'>[]) => {
          const target = context?.[0]?.label
          const dateTarget = target ? parseDateString(target) : new Date()
          const startHour = format(dateTarget, 'ha')
          const endHour = format(add(dateTarget, { hours: 1 }), 'ha')
          const hourRange = `${startHour} - ${endHour}`
          return hourRange
        },
        label: function (context: TooltipItem<'bar'>) {
          const category = context?.dataset?.label || ''
          const categoryCount = context?.parsed?.y ?? 0

          if (categoryCount === 0) return null

          return `${categoryCount}% ${category}`
        },
      },
    },
  },
  scales: {
    x: {
      display: false,
    },
    y: {
      ...afterBuildTicksIgnoringNulls,
      ...defaultGridColor,
      ticks: {
        font: {
          family: 'Roboto Mono',
        },
        callback: (tickValue: string | number): string => {
          if (typeof tickValue === 'string') return `${tickValue}%`
          const formatedValue = new Intl.NumberFormat('en-US', {
            style: 'unit',
            unit: 'percent',
            maximumFractionDigits: 1,
          }).format(tickValue)

          return formatedValue
        },
      },
    },
  },
}

export const PERCENT_UNIT_DAILY_ACCURACY_LINE_OPTIONS = Object.assign({}, PERCENT_UNIT_DAILY_LINE_OPTIONS)

PERCENT_UNIT_DAILY_ACCURACY_LINE_OPTIONS.scales.y.ticks.font = {
  family: 'Roboto Mono',
}

PERCENT_UNIT_DAILY_ACCURACY_LINE_OPTIONS.scales.y.ticks.callback = (tickValue: string | number): string => {
  if (typeof tickValue === 'string') return `${tickValue}%`
  return new Intl.NumberFormat('en-US', {
    style: 'unit',
    unit: 'percent',
    maximumFractionDigits: 1,
  })
    .format(tickValue)
    .padStart(5, ' ')
}

export const MULTIPLE_LINE_OPTIONS = {
  ...defaultLineLayout,
  plugins: {
    legend: {
      display: false,
    },
    tooltip: aggregatedTooltipWithColors,
  },
  interaction: {
    mode: 'nearest',
    axis: 'x',
    intersect: false,
  },
  scales: {
    x: {
      afterBuildTicks: (axis: LinearScale) => {
        if (axis.chart.config.data.labels) {
          axis.ticks = buildXAxisLabels(axis.chart.config.data.labels.map((l) => new Date(l as string)))
        }
      },
      type: 'time',
      autoSkip: true,
      time: {
        displayFormats: {
          day: 'MM/dd',
        },
        adapters: {
          locale: '',
        },
      },
      grid: {
        display: false,
      },
    },
    y: {
      afterBuildTicks: (axis: LinearScale) => {
        const displayLabels = buildMultilineYAxisLabels(
          axis.chart.config.data.datasets as unknown as LineChartDataset[]
        )
        axis.ticks = displayLabels
      },
      ...defaultGridColor,
      min: 0,
      ticks: {
        callback: (tickValue: string | number): string => `${tickValue}s`,
      },
    },
  },
}

export const PREDICTION_LINE_OPTIONS = {
  ...defaultLineLayout,
  plugins: {
    legend: {
      display: false,
    },
    tooltip: {
      ...commonTooltipProps,
      displayColors: false,

      callbacks: {
        title: (context: TooltipItem<'line'>[]) => {
          const lastHour = parseInt(context?.[0]?.label) + 1 === 24
          return `${convertToAmPm(parseInt(context?.[0]?.label))} - ${convertToAmPm(
            lastHour ? 0 : parseInt(context?.[0]?.label) + 1
          )}`
        },
        label: function (context: TooltipItem<'line'>) {
          const position = (context.element as any)['$context'].dataIndex
          const ref = (context.dataset as any).ref
          if (!ref) return ref

          const list: { label: string; values: { type: string; currentValue: number }[] }[] = []

          ref.forEach((ref: any) => {
            const normalized = ref.values.map((x: any) => {
              const currentValue = x.data[position]
              return {
                type: x.type ?? 'None',
                currentValue: Number.isInteger(currentValue) ? currentValue : currentValue.toFixed(0),
              }
            })

            list.push({ label: ref.label, values: normalized })
          })

          let newList = list.sort((a, b) => a.label.localeCompare(b.label))
          newList = newList.filter((x) => x.values.filter((x) => x.currentValue > 0).length > 0)

          if (newList.length > 5) {
            const rest = newList.slice(5)
            newList = newList.slice(0, 5)

            const valuesDic: { [key: string]: number } = {}

            rest.forEach((x) => {
              x.values.forEach((y) => {
                const type = y.type ?? 'Other'

                if (valuesDic[type]) valuesDic[type] += y.currentValue
                else valuesDic[type] = y.currentValue
              })
            })

            const values = Object.keys(valuesDic).map((x) => ({ type: x, currentValue: valuesDic[x] }))

            newList.push({ label: 'Other', values })
          }

          return newList
        },
        beforeBody: (context: [TooltipItem<'line'>, TooltipItem<'line'>]) => {
          const position = (context[0].element as any)['$context'].dataIndex
          const dataset = context[0].dataset as any

          let total = dataset.data[position]
          if (!total) {
            total = 0
          } else if (!Number.isInteger(total)) {
            total = parseFloat(total.toFixed(2))
          }
          const prevTotal = position > 0 ? dataset.data[position - 1] : 0

          const previousFinalValue = Number.isInteger(prevTotal)
            ? prevTotal
            : parseFloat(prevTotal.toFixed(2))

          const isNeutral = total === previousFinalValue

          if (isNeutral) return [total, 0, 'neutral']
          if (previousFinalValue === 0) return [total, 100, 'positive']

          const variation = ((total - previousFinalValue) / previousFinalValue) * 100
          const variationStatus = variation > 0 ? 'positive' : 'negative'
          const percentage = Number.isInteger(variation)
            ? Math.abs(variation)
            : Math.abs(parseFloat(variation.toFixed(2)))

          return [total, percentage, variationStatus]
        },
      },
      interaction: {
        mode: 'index',
        intersect: false,
      },
      enabled: false,
      position: 'topFixedPositionTooltip',
      external: externalTooltipHandler,
      yAlign: 'bottom',
    },
  },
  scales: {
    x: {
      ticks: {
        callback: (value: number) => convertToAmPm(value),
      },
      afterBuildTicks: (axis: LinearScale) => {
        if (axis.chart.config.data.labels) {
          axis.ticks = buildXAxisHourNumbersLabels(axis.chart.config.data.labels.map((l) => l as number))
        }
      },
      autoSkip: true,
      grid: {
        borderColor: '#F3F4F6',
        color: 'rgba(255,255,255, 1)',
        tickColor: '#F3F4F6',
      },
    },
    y: {
      afterBuildTicks: (axis: LinearScale) => {
        const displayLabels = buildYAxisLabels(
          axis.chart.config.data.datasets[0] as unknown as LineChartDataset
        )
        axis.ticks = displayLabels
      },
      ...defaultGridColor,
      min: 0,
    },
  },
}

export const STACKED_LINE_OPTIONS_WITH_DATE = {
  ...STACKED_LINE_OPTIONS_DEFAULT,
}

export const STACKED_LINE_OPTIONS_WITH_DATE_WEEKLY = () => {
  let weeklyHelpLabels: string[] = []
  const options = {
    ...STACKED_LINE_OPTIONS_DEFAULT,
    plugins: {
      ...STACKED_LINE_OPTIONS_DEFAULT.plugins,
      tooltip: {
        ...STACKED_LINE_OPTIONS_DEFAULT.plugins.tooltip,
        callbacks: {
          ...STACKED_LINE_OPTIONS_DEFAULT.plugins.tooltip.callbacks,
          title: (context: TooltipItem<'bar'>[]) => {
            const dateString = context[0]?.label
            const startDateFormatted = dateString
              ? format(new Date(dateString.split('-')[0].trim()), 'dd/MM/yyyy')
              : ''
            const endDateFormatted = dateString
              ? format(new Date(dateString.split('-')[1].trim()), 'dd/MM/yyyy')
              : ''

            return `${startDateFormatted} - ${endDateFormatted}`
          },
        },
      },
    },
    scales: {
      ...STACKED_LINE_OPTIONS_DEFAULT.scales,
      x: {
        ...STACKED_LINE_OPTIONS_DEFAULT.scales.x,
        afterBuildTicks: (axis: LinearScale) => {
          weeklyHelpLabels = buildXAxisWeeklyLabels(axis.chart.config.data.labels as string[])
        },
        ticks: {
          callback(tickValue: number) {
            return weeklyHelpLabels[tickValue]
          },
        },
        type: 'category',
        time: null,
        adapters: null,
      },
    },
  }

  return options
}

export const LINEAR_SCALE_LINE_OPTIONS = {
  animation: animation,
  responsive: true,
  elements: {
    line: {
      tension: 0.5,
    },
    point: {
      radius: 4,
      hitRadius: 0,
      hoverRadius: 0,
    },
  },
  interaction: {
    mode: 'nearest',
    axis: 'x',
    intersect: false,
  },
  plugins: {
    legend: {
      display: false,
    },
    tooltip: {
      ...commonTooltipProps,
      /* formats display methods */
      callbacks: {
        ...defaultTooltipCallbacks,
        label: function (context: TooltipItem<'bar'>) {
          const categoryCount = context?.parsed?.y ?? 0
          if (categoryCount === 0) return null

          return `${categoryCount}%`
        },
      },
      yAlign: 'bottom',
      position: 'topFixedPositionTooltip',
    },
  },
  scales: {
    x: {
      afterBuildTicks: (axis: LinearScale) => {
        axis.ticks = buildXAxisLabels(axis.chart.config.data.labels as Date[])
      },
      type: 'time',
      autoSkip: true,
      time: {
        displayFormats: {
          day: 'MM/dd',
        },
        adapters: {
          locale: '',
        },
      },
      grid: {
        display: false,
      },
    },
    y: {
      ...defaultGridColor,
      afterBuildTicks: (axis: LinearScale) => {
        axis.ticks = [axis.min, Math.ceil((axis.min + axis.max) / 2 / 10) * 10, axis.max].map((v) => ({
          value: v,
        }))
      },
      ticks: {
        callback: (value: string) => `${value}%`,
      },
    },
  },
}

export const LINEAR_SCALE_LINE_WEEKLY_OPTIONS = () => {
  let weeklyHelpLabels: string[] = []

  return {
    ...LINEAR_SCALE_LINE_OPTIONS,
    scales: {
      ...LINEAR_SCALE_LINE_OPTIONS.scales,
      x: {
        ...LINEAR_SCALE_LINE_OPTIONS.scales.x,
        afterBuildTicks: (axis: LinearScale) => {
          weeklyHelpLabels = buildXAxisWeeklyLabels(axis.chart.config.data.labels as string[])
        },
        ticks: {
          callback(tickValue: number) {
            return weeklyHelpLabels[tickValue]
          },
        },
        type: 'category',
        time: null,
        adapters: null,
      },
    },
  }
}

export const STACKED_DAILY_LINE_OPTIONS_WITH_DATE = {
  ...STACKED_LINE_OPTIONS_DEFAULT,
  plugins: {
    ...STACKED_LINE_OPTIONS_DEFAULT.plugins,
    tooltip: {
      ...STACKED_LINE_OPTIONS_DEFAULT.plugins.tooltip,
      /* formats display methods */
      callbacks: {
        ...STACKED_LINE_OPTIONS_DEFAULT.plugins.tooltip.callbacks,
        title: (context: TooltipItem<'bar'>[]) => {
          const target = context?.[0]?.label
          const dateTarget = target ? parseDateString(target) : new Date()
          const startHour = format(dateTarget, 'ha')
          const endHour = format(add(dateTarget, { hours: 1 }), 'ha')
          const hourRange = `${startHour} - ${endHour}`

          return hourRange
        },
      },
    },
  },
}

export const STACKED_LINE_OPTIONS_WITH_HOURS = {
  ...STACKED_LINE_OPTIONS_DEFAULT,
  plugins: {
    ...STACKED_LINE_OPTIONS_DEFAULT.plugins,
    tooltip: {
      ...STACKED_LINE_OPTIONS_DEFAULT.plugins.tooltip,
      callbacks: {
        ...STACKED_LINE_OPTIONS_DEFAULT.plugins.tooltip.callbacks,
        title: (context: TooltipItem<'bar'>[]) =>
          context?.[0]?.label
            ? format(new Date(context?.[0]?.label.replace('.', '')), 'MM/dd/yyyy hh:mm a')
            : '',
      },
    },
  },
  scales: {
    ...STACKED_LINE_OPTIONS_DEFAULT.scales,
    x: {
      ...STACKED_LINE_OPTIONS_DEFAULT.scales.x,
      afterBuildTicks: (axis: LinearScale) => {
        axis.ticks = buildXAxisLabels(axis.chart.config.data.labels as Date[], { qtd: 5, format: 'h a' })
      },
      time: {
        ...STACKED_LINE_OPTIONS_DEFAULT.scales.x.time,
        displayFormats: {
          ...STACKED_LINE_OPTIONS_DEFAULT.scales.x.time.displayFormats,
          day: 'h a',
        },
      },
    },
  },
}

export const DATA_OPTION = {
  ...DEFAULT_LINE_OPTIONS,
  plugins: {
    ...DEFAULT_LINE_OPTIONS.plugins,
    tooltip: {
      ...commonTooltipProps,
      callbacks: {
        title: (context: TooltipItem<'bar'>[]) => {
          const target = context?.[0]?.label.split(',').slice(0, 2).join(', ')
          // @ts-expect-error using a custom prop
          const title = context?.[0]?.dataset?.title || ''
          const formattedDate = format(new Date(target), 'MM/dd/yyyy')

          return `${formattedDate}\n${title}`
        },

        beforeBody: (context: [TooltipItem<'bar'>, TooltipItem<'bar'>]) => {
          return `${context.reduce((curr, value) => curr + value.parsed.y, 0).toFixed(1)} Total`
        },
        label: function (context: TooltipItem<'bar'>) {
          const label = (context?.dataset?.label || '').toString()
          const categoryCount = context?.parsed?.y ?? 0

          if (categoryCount === 0) return null

          return `${categoryCount.toFixed(1)}${label}`
        },
      },
      yAlign: 'bottom',
    },
  },
  scales: {
    ...STACKED_LINE_OPTIONS_DEFAULT.scales,
  },
}

export const CURRENT_DAY_MERGED_BAR_LINE_OPTIONS = {
  animation: animation,
  barThickness: 8,
  elements: {},
  borderRadius: (ctx: any) => {
    const dataPoint = ctx.dataIndex
    const datasetIndex = ctx.datasetIndex

    const values = ctx.chart.data.datasets.map((dataset: any) => dataset.data[dataPoint])
    const activeValues = values.filter((val: any) => val !== null && val !== undefined && val !== 0).length

    if (activeValues === 1) {
      return {
        topLeft: 10,
        topRight: 10,
        bottomLeft: 10,
        bottomRight: 10,
      }
    }

    const isFirst =
      values.findIndex((val: any) => val !== null && val !== undefined && val !== 0) === datasetIndex
    const isLast =
      values
        .slice()
        .reverse()
        .findIndex((val: any) => val !== null && val !== undefined && val !== 0) ===
      values.length - 1 - datasetIndex

    if (isFirst) return { bottomLeft: 10, bottomRight: 10 }
    if (isLast) return { topLeft: 10, topRight: 10 }

    return { topLeft: 0, topRight: 0, bottomLeft: 0, bottomRight: 0 }
  },
  interaction: {
    intersect: true,
    mode: 'index',
  },
  layout: {
    padding: {
      right: 14,
    },
  },
  plugins: {
    legend: {
      display: false,
    },
    tooltip: {
      ...commonTooltipProps,
      displayColors: false,
      callbacks: {
        title: (context: TooltipItem<'bar'>[]) => {
          const label = context?.[0]?.label ?? ''
          const hour = parseInt(label)
          const lastHour = hour + 1 === 24
          const startHour = convertToAmPm(hour)
          const endHour = convertToAmPm(lastHour ? 0 : hour + 1)
          const datasetTimezone = (context?.[0]?.dataset as { timezone?: string } | undefined)?.timezone
          const timezone = datasetTimezone ?? (label.includes('UTC') ? 'UTC' : '')
          const timezoneLabel = timezone ? ` ${timezone}` : ''
          return `${startHour} - ${endHour}${timezoneLabel}`
        },
        beforeBody: (context: TooltipItem<'bar'>[]) => {
          const tasksDataset = context.find((item) => item.dataset.label === 'Tasks')
          const tasksCount = tasksDataset?.parsed?.y ?? 0

          const barDatasets = context.filter((item) => item.dataset.label !== 'Tasks')
          const currentTotal = barDatasets.reduce((acc, curr) => acc + (curr?.parsed?.y ?? 0), 0)

          const previousTotal = barDatasets.reduce(
            (acc, curr) => acc + ((curr?.dataset?.data[curr.dataIndex - 1] as number) ?? 0),
            0
          )

          const isPrediction = (context[0].dataset as any).predictionIndex <= context[0].dataIndex

          const previousFinalValue = Number.isInteger(previousTotal)
            ? previousTotal
            : parseFloat(previousTotal.toFixed(2))

          const isNeutral = currentTotal === previousFinalValue
          if (isNeutral || previousFinalValue === 0) {
            return [currentTotal, 0, 'neutral', isPrediction, tasksCount > 0 ? tasksCount : null]
          }

          const variation = ((currentTotal - previousFinalValue) / previousFinalValue) * 100
          const variationStatus = variation > 0 ? 'positive' : 'negative'
          const percentage = Number.isInteger(variation)
            ? Math.abs(variation)
            : Math.abs(parseFloat(variation.toFixed(2)))

          return [currentTotal, percentage, variationStatus, isPrediction, tasksCount > 0 ? tasksCount : null]
        },
        label: function (context: TooltipItem<'bar'>) {
          if (context.datasetIndex !== 0) return null

          const currentIndex = context.dataIndex
          const allDatasets = context.chart.data.datasets.filter((ds: any) => ds.label !== 'Tasks')

          const currentTotal = allDatasets.reduce((acc: number, dataset: any) => {
            return acc + ((dataset.data[currentIndex] as number) ?? 0)
          }, 0)

          const isPrediction = (context.dataset as any).predictionIndex <= context.dataIndex

          const hasSurgeAlert = (context.dataset as any).alerts?.surgeAlertHours?.includes(currentIndex)
          const hasCapacityAlert = (context.dataset as any).alerts?.capacityAlertsHours?.includes(
            currentIndex
          )

          const list: {
            label: string
            total: number
            color?: string
            iconName?: string
            values: any[]
            sortIndex?: number
          }[] = []

          if (hasSurgeAlert) {
            list.push({
              label: 'Surge Alert',
              total: 0,
              iconName: 'surge-alert',
              values: [],
              sortIndex: -2,
            })
          }

          if (hasCapacityAlert) {
            list.push({
              label: 'Capacity Alert',
              total: 0,
              iconName: 'capacity-alert',
              values: [],
              sortIndex: -1,
            })
          }

          if (!isPrediction) {
            const sortOrder = {
              'High Priority': 1,
              'Low Priority': 2,
              'Timed Out': 3,
              Rejected: 4,
              Recycled: 5,
            }

            const color = {
              'High Priority': '#6EE7B7',
              'Low Priority': '#5A86AE',
              Recycled: '#7FB6B1',
              Rejected: '#EDC948',
              'Timed Out': '#B6BDC6',
            }

            const datasetItems = allDatasets
              .map((dataset: any) => {
                const value = (dataset.data[currentIndex] as number) ?? 0
                const percentage =
                  currentTotal === 0 ? 0 : parseFloat(((value / currentTotal) * 100).toFixed(0))

                const label = dataset.label
                  ?.replace('High Priority Queue', 'High Priority')
                  .replace('Low Priority Queue', 'Low Priority')

                return {
                  label: `${label}: ${percentage}% (${value})`,
                  total: value,
                  color: color[label as keyof typeof color],
                  values: [],
                  sortIndex: label ? sortOrder[label as keyof typeof sortOrder] : 999,
                }
              })
              .filter((item: any) => item.total > 0)
              .sort((a: any, b: any) => a.sortIndex - b.sortIndex)

            list.push(...datasetItems)
          }

          const msOnline = (context.dataset as any).msOnline?.[currentIndex] ?? 0
          const msStatus = !isPrediction ? 'online' : 'scheduled'

          list.push({
            label: `Mission Specialists ${msStatus}: ${msOnline}`,
            total: msOnline,
            values: [],
            sortIndex: 999,
          })

          return list
        },
      },
      enabled: false,
      external: (context: any) =>
        externalTooltipHandler(context, {
          customTotalTitle: 'Total Engagements',
          customTotalPredictionTitle: 'Predicted Engagements',
          noBodyLabelEllipsis: true,
          moveFirstBar: 20,
          reverseTrendingComparison: true,
        }),
    },
  },
  responsive: true,
  scales: {
    x: {
      autoSkip: true,
      stacked: true,
      grid: {
        borderColor: 'white',
        color: 'rgba(255,255,255, 1)',
        tickColor: 'white',
      },
      ticks: {
        padding: 32,
      },
    },
    leftYAxis: {
      afterBuildTicks: (axis: LinearScale) => {
        const datasets = axis.chart.config.data.datasets as { data: number[]; yAxisID?: string }[]

        if (datasets.length === 0) return

        const barDatasets = datasets.filter((ds) => ds.yAxisID === 'leftYAxis')
        const syntheticDatasets = {
          data: barDatasets[0].data.map((_, index) =>
            barDatasets.reduce((sum, dataset) => sum + (dataset.data[index] || 0), 0)
          ),
        }
        axis.ticks = buildYAxisLabels(syntheticDatasets as unknown as BarChartDataset)
      },
      stacked: true,
      position: 'left',
      grid: {
        borderColor: 'white',
        color: 'rgba(243, 244, 246, 1)',
        tickColor: 'white',
      },
      ticks: {
        callback(tickValue: number) {
          return tickValue.toLocaleString('en-US')
        },
      },
    },
    rightYAxis: {
      position: 'right',
      grid: {
        display: false,
      },
      ticks: {
        callback(tickValue: number) {
          return tickValue.toLocaleString('en-US')
        },
      },
    },
  },
}

export const DATA_WEEKLY_OPTION = () => {
  let weeklyHelpLabels: string[] = []

  const options = {
    ...DEFAULT_LINE_OPTIONS,
    plugins: {
      ...DEFAULT_LINE_OPTIONS.plugins,
      tooltip: {
        ...commonTooltipProps,
        callbacks: {
          title: (context: TooltipItem<'bar'>[]) => {
            const target = context?.[0]?.label.split(',').slice(0, 2).join(', ')
            // @ts-expect-error using a custom prop
            const title = context?.[0]?.dataset?.title || ''
            const split = target.split('|')
            const startDate = new Date(split[0].trim())
            const endDate = new Date(split[1].trim())
            const formattedDate = `${format(new Date(startDate), 'MM/dd/yyyy')} - ${format(new Date(endDate), 'MM/dd/yyyy')}`

            return `${formattedDate}\n${title}`
          },

          beforeBody: (context: [TooltipItem<'bar'>, TooltipItem<'bar'>]) => {
            return `${context.reduce((curr, value) => curr + value.parsed.y, 0).toFixed(1)} Total`
          },
          label: function (context: TooltipItem<'bar'>) {
            const label = (context?.dataset?.label || '').toString()
            const categoryCount = context?.parsed?.y ?? 0

            if (categoryCount === 0) return null

            return `${categoryCount.toFixed(1)}${label}`
          },
        },
        yAlign: 'bottom',
      },
    },
    scales: {
      ...STACKED_LINE_OPTIONS_DEFAULT.scales,
      x: {
        ...STACKED_LINE_OPTIONS_DEFAULT.scales.x,
        afterBuildTicks: (axis: LinearScale) => {
          weeklyHelpLabels = buildXAxisWeeklyLabels(axis.chart.config.data.labels as string[])
        },
        ticks: {
          callback(tickValue: number) {
            return weeklyHelpLabels[tickValue]
          },
        },
        type: 'category',
        time: null,
        adapters: null,
      },
    },
  }

  return options
}
